import { TableBody } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import useBusy from "@/components/data-table/hooks/useBusy";
import useRowVirtualizer from "@/components/data-table/hooks/useRowVirtualizer";
import { useVirtualRowStyle } from "@/components/useVirtualRowStyles";
import { Table } from "@tanstack/react-table";
import { useCallback, useMemo, useRef } from "react";
import { useCheckIsProjectLocked } from "../hooks/useCheckIsProjectLocked";
import PeriodsTableRow from "./PeriodsTableRow";
import EmptyState from "@/components/data-table/components/EmptyState";

interface PeriodsTableBodyProps<TData extends { id: string }> {
  table: Table<TData>;
  onRowMouseEnter?: (rowId: string) => void;
  onRowMouseLeave?: (rowId: string) => void;
}

function PeriodsTableBody<TData extends { id: string }>({
  table,
  onRowMouseEnter,
  onRowMouseLeave,
}: PeriodsTableBodyProps<TData>) {
  const bodyRef = useRef(null);
  const defaultClassName = "grid relative overflow-visible";
  const rows = table.getRowModel().rows;

  const onRowMouseEnterInternal = useCallback(
    (rowId: string) => {
      return () => {
        onRowMouseEnter?.(rowId);
      };
    },
    [onRowMouseEnter],
  );

  const onRowMouseLeaveInternal = useCallback(
    (rowId: string) => {
      return () => {
        onRowMouseLeave?.(rowId);
      };
    },
    [onRowMouseLeave],
  );

  const isBusy = useBusy();
  const isProjectLocked = useCheckIsProjectLocked();

  const rowVirtualizer = useRowVirtualizer(rows.length, bodyRef);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  useVirtualRowStyle(rowVirtualizer, bodyRef);

  const style = useMemo(() => {
    const hasSize = totalSize > 0;

    return {
      height: hasSize ? `${totalSize}px` : "178px", // tells scrollbar how big the table is
      ...(!hasSize && { paddingTop: "18px" }),
    };
  }, [totalSize]);

  return (
    <TableBody
      className={cn(defaultClassName, {
        "opacity-50 pointer-events-none blur-[2px]": isBusy,
      })}
      style={style}
      ref={bodyRef}
    >
      {virtualRows.length === 0 && <EmptyState />}
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (row === undefined) {
          return null;
        }

        return (
          <PeriodsTableRow
            key={row.id}
            row={row}
            cells={row.getVisibleCells()}
            onRowMouseEnterInternal={onRowMouseEnterInternal}
            onRowMouseLeaveInternal={onRowMouseLeaveInternal}
            virtualRow={virtualRow}
            isBusy={isBusy ?? isProjectLocked}
          />
        );
      })}
    </TableBody>
  );
}

export default PeriodsTableBody;
